import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { useAuthenticationStore } from '$/components/core/Authentication/stores/useAuthenticationStore';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { useMaterialGridMaxItems } from '$/hooks/useMaterialGridMaxItems';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { ProjectFolderItem } from '$/pages/DashboardPages/pages/Projects/components/ProjectFolderItem';
import { ProjectModal } from '$/pages/DashboardPages/pages/Projects/components/ProjectModal';
import { AddProjectFolderButton } from '$/pages/DashboardPages/pages/Welcome/components/ProjectFolderSection/components/AddProjectFolderButton';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import {
  projectFoldersQuery,
  projectsQuery,
} from '$/services/usecases/projects/queries';
import { useLastSeenStore } from '$/stores/useLastSeenStore';

export const ProjectFolderSection = () => {
  const { t } = useTranslation();
  const itemLimit = useMaterialGridMaxItems();

  const user = useAuthenticationStore.useUser();
  const { data: projectsData } = useQuery(
    projectsQuery({ enabled: user != null }),
  );
  const { data: projectFoldersData } = useQuery(projectFoldersQuery());
  const lastSeenFolders = useLastSeenStore.useLastSeenProjectFolders();

  const sortFolders = (items: ProjectFolder[]) => {
    return items.sort((a, b) => {
      const aLastSeenIndex = lastSeenFolders.findIndex(
        (folder) => folder.id === a.id,
      );
      const bLastSeenIndex = lastSeenFolders.findIndex(
        (folder) => folder.id === b.id,
      );

      if (aLastSeenIndex === -1 && bLastSeenIndex === -1) return 0;
      if (aLastSeenIndex === -1) return 1;
      if (bLastSeenIndex === -1) return -1;
      return bLastSeenIndex - aLastSeenIndex;
    });
  };

  const shownFolders = sortFolders(projectFoldersData?.response.payload ?? [])
    .map((folder) => ({
      ...folder,
      imageUrl: projectsData?.response.payload.find(
        (project) => project.parentId === folder.id,
      )?.thumbnailUrl,
    }))
    .slice(0, itemLimit - 1);

  return (
    <SectionHeadline
      headline={t('dashboard.welcome.projectFoldersText')}
      showAllUrl='/projects'
    >
      <DashboardSectionRow>
        {shownFolders.map((folder) => (
          <ProjectFolderItem
            projectFolder={folder}
            key={folder.id}
            imageUrl={folder.imageUrl}
          />
        ))}
        <AddProjectFolderButton />
      </DashboardSectionRow>
      <ProjectModal />
    </SectionHeadline>
  );
};
